// Minimal mirrors of the Fabrika API shapes the phone client renders.

export interface Decision {
  id: string;
  planId: string;
  taskId: string;
  question: string;
  options: string[];
  context: string;
  status: string;
}

export interface Contract {
  verifyCmds: string[];
  heldOut: string[];
  properties: string[];
  lockedGlobs: string[];
  heldOutFiles?: Record<string, string>;
}

export interface Task {
  id: string;
  title: string;
  spec: string;
  riskTier: string;
  status: string;
  agentId: string;
  branch: string;
  acceptance: Contract;
  dependsOn: string[];
  touchPaths: string[];
  tags: string[];
  priority: string;
}

export interface StageResult {
  pass: boolean;
  output: string;
  skipped: boolean;
  exitCode: number;
}

export interface Evidence {
  stages?: Record<string, StageResult>;
  diff?: string;
  artifacts?: string[];
}

// Usage mirrors internal/model.Usage: token usage an agent self-reports.
export interface Usage {
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
}

export interface Attempt {
  id?: string;
  result?: string;
  evidence?: Evidence;
  usage?: Usage;
  // The agent's own log — carries crashes that abort before any gate runs.
  log?: string;
}

export interface Comment {
  id: string;
  taskId: string;
  authorType: string; // "system", "user", or an agent id
  authorId: string;
  body: string;
  attachments?: string[];
  createdAt: string;
}

// Gate stages in run order — mirrors Fabrika's STAGE_ORDER so pills and
// failure details read in the same sequence as the desktop UI.
export const STAGE_ORDER = ["setup", "typecheck", "lint", "build", "test", "verify", "e2e", "review", "mutation"];

export interface ReviewItem {
  task: Task;
  attempt: Attempt | null;
}

export interface BigTask {
  id: string;
  title: string;
  intent: string;
  status: string;
  error: string;
}

export interface Plan {
  id: string;
  bigTaskId: string;
  tasks: Task[];
  status: string;
  bigTask: BigTask | null;
}

export interface Attention {
  reviews: ReviewItem[];
  decisions: Decision[];
  audits: ReviewItem[];
  plans: Plan[];
}

export const ROLES = ["implementer", "planner", "reviewer"];

// An agent registered on the daemon. There is no `kind` field. Roles are a
// subset of "implementer" | "planner" | "reviewer" (the daemon rejects
// anything else); name and command must be non-empty, and timeout (if set) is
// a Go duration string like "20m".
export interface Agent {
  id: string;
  name: string;
  photo?: string;
  command: string;
  model: string;
  roles: string[];
  tags: string[];
  concurrency: number;
  timeout: string;
  maxAttempts: number;
  enabled: boolean;
  priority: number;
}
